import { useCallback, useEffect, useMemo } from 'react';

import { useLocation } from 'react-router';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import { useClearedQueryParamValue, useQueryParamValue } from 'src/app/shared/hooks/location';
import { useLoginState } from 'src/app/shared/hooks/session';
import { usePendingSignature } from 'src/app/shared/hooks/signature';

type Result = ModelHookResult<
	{
		message?: string;
		guestMessage?: string;
	},
	{
		trackGuestMessageLinkClick: () => void;
	}
>;

export function usePageBanner(): Result {
	const track = useTracking();

	const guestMessage = useGuestMessage();

	useEffect(() => {
		if (guestMessage) {
			void track('post_sign_verify_banner_view');
		}
	}, [guestMessage, track]);

	return {
		data: {
			message: useMessage(),
			guestMessage,
		},
		actions: {
			trackGuestMessageLinkClick: useCallback(() => {
				void track('post_sign_verify_banner_click');
			}, [track]),
		},
	};
}

function useMessage() {
	const message = useClearedQueryParamValue('message');
	const { translate } = useI18n();

	return useMemo(() => {
		switch (message) {
			// coming from /sign-now (email one-click sign)
			case 'sign-success':
				return translate('fe.components.psf.promote_or_share.single_click.sign_success');
			default:
				return undefined;
		}
	}, [message, translate]);
}

function useGuestMessage() {
	const guestQuery = useQueryParamValue('guest');
	const {
		data: {
			petition: { id },
		},
	} = usePsfSharePageData();
	const { translate } = useI18n();
	const [pendingSignature] = usePendingSignature(id);
	const location = useLocation();
	const loginState = useLoginState();

	const loginUrl = `/login_or_join?redirectTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

	const addLoginUrl = useCallback(
		(guestMessage: string) => {
			// FIXME add %{href} to the i18n values
			if (guestMessage.includes('href=')) return guestMessage;
			return guestMessage.replace('<a ', `<a href="${loginUrl}" `);
		},
		[loginUrl],
	);

	return useMemo(() => {
		if (loginState !== 'GUEST' || !guestQuery || !['new', 'existing'].includes(guestQuery) || !pendingSignature)
			return undefined;
		const replacements = {
			// FIXME remove %{link_classes} from the i18n values
			link_classes: '',
			// FIXME add %{href} to the i18n values
			href: loginUrl,
			user_email: pendingSignature.userAccountData.email,
		};
		if (guestQuery === 'new') return translate('fe.components.psf.guest_login_banner.new_user_verify', replacements);
		if (pendingSignature.userAccountData.connected_to_facebook)
			return addLoginUrl(translate('fe.components.psf.guest_login_banner.login_via_facebook', replacements));
		if (pendingSignature.userAccountData.password_set)
			return addLoginUrl(translate('fe.components.psf.guest_login_banner.login_via_change', replacements));
		return translate('fe.components.psf.guest_login_banner.login_via_email_link', replacements);
	}, [loginState, guestQuery, pendingSignature, loginUrl, translate, addLoginUrl]);
}
